import React, { useState } from 'react'
import styled from 'styled-components'
import { Icon } from '../../../../../atoms/Icon'
import { Loading } from '../../../../../atoms/Loading'
import { ExpandArrow } from '../../../../../atoms/ExpandArrow'

const SectionContainer = styled.div`
  border-bottom: 1px solid ${({ theme }) => theme.colors.border.light};
`

const SectionHeader = styled.button<{ $isExpanded: boolean }>`
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: ${({ theme }) => theme.spacing.default};
  background: ${({ $isExpanded, theme }) =>
    $isExpanded
      ? theme.colors.background.secondary
      : theme.colors.background.primary};
  border: none;
  cursor: pointer;
  transition: all ${({ theme }) => theme.transitions.fast};

  &:hover {
    background: ${({ theme }) => theme.colors.background.secondary};
  }

  &:focus-visible {
    outline: 2px solid ${({ theme }) => theme.colors.primary};
    outline-offset: -2px;
  }
`

const SectionHeaderContent = styled.div`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing.small};
`

const SectionIcon = styled.div`
  display: flex;
  align-items: center;
  color: ${({ theme }) => theme.colors.primary};
`

const SectionTitle = styled.span`
  font-size: ${({ theme }) => theme.typography.fontSize.body};
  font-weight: ${({ theme }) => theme.typography.fontWeight.medium};
  color: ${({ theme }) => theme.colors.text.primary};
  text-align: left;
`

const SectionCount = styled.span`
  font-size: ${({ theme }) => theme.typography.fontSize.small};
  color: ${({ theme }) => theme.colors.text.tertiary};
  margin-left: ${({ theme }) => theme.spacing.xsmall};
`

const SectionContent = styled.div<{ $isExpanded: boolean }>`
  max-height: ${({ $isExpanded }) => ($isExpanded ? '1000px' : '0')};
  overflow: hidden;
  transition: max-height ${({ theme }) => theme.transitions.default};
  overflow-y: auto;
`

const SectionBody = styled.div`
  padding: ${({ theme }) => theme.spacing.small} 0;
`

const LoadingContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  padding: ${({ theme }) => theme.spacing.large};
`

const ErrorMessage = styled.div`
  padding: ${({ theme }) => theme.spacing.default};
  color: ${({ theme }) => theme.colors.danger};
  font-size: ${({ theme }) => theme.typography.fontSize.small};
`

const EmptyMessage = styled.div`
  padding: ${({ theme }) => theme.spacing.default};
  color: ${({ theme }) => theme.colors.text.tertiary};
  font-size: ${({ theme }) => theme.typography.fontSize.small};
  text-align: center;
`

export interface DocumentSectionTranslations {
  noDocuments?: string
}

const DEFAULT_TRANSLATIONS: DocumentSectionTranslations = {
  noDocuments: 'No documents available',
}

interface DocumentSectionProps {
  title: string
  icon: string
  count?: number
  isLoading?: boolean
  error?: string
  defaultExpanded?: boolean
  onExpand?: () => void
  children: React.ReactNode
  translations?: DocumentSectionTranslations
}

export const DocumentSection: React.FC<DocumentSectionProps> = ({
  title,
  icon,
  count,
  isLoading = false,
  error,
  defaultExpanded = false,
  onExpand,
  children,
  translations,
}) => {
  const t = { ...DEFAULT_TRANSLATIONS, ...translations }
  const [isExpanded, setIsExpanded] = useState(defaultExpanded)

  const handleToggle = () => {
    const newExpandedState = !isExpanded
    setIsExpanded(newExpandedState)

    // Call onExpand when expanding for the first time
    if (newExpandedState && onExpand) {
      onExpand()
    }
  }

  return (
    <SectionContainer>
      <SectionHeader
        onClick={handleToggle}
        $isExpanded={isExpanded}
        type="button"
        aria-expanded={isExpanded}
      >
        <SectionHeaderContent>
          <SectionIcon>
            <Icon name={icon as any} size="small" />
          </SectionIcon>
          <SectionTitle>
            {title}
            {count !== undefined && <SectionCount>({count})</SectionCount>}
          </SectionTitle>
        </SectionHeaderContent>
        <ExpandArrow $expanded={isExpanded} direction="down" />
      </SectionHeader>

      <SectionContent $isExpanded={isExpanded}>
        <SectionBody>
          {isLoading ? (
            <LoadingContainer>
              <Loading size="small" />
            </LoadingContainer>
          ) : error ? (
            <ErrorMessage>{error}</ErrorMessage>
          ) : count === 0 ? (
            <EmptyMessage>{t.noDocuments}</EmptyMessage>
          ) : (
            children
          )}
        </SectionBody>
      </SectionContent>
    </SectionContainer>
  )
}
